import React, { useMemo, useState } from "react";
import Text from "components/Text";
import { getTimesheetStatusById } from "./../../../constants";
import Form from "./Form";
import ClockInClockOutForm from "./CockInClockOutForm";
import styles from "./Timesheet.module.scss";
import { decodeId, formatDate } from "utils";
import { AnimatePresence, motion } from "framer-motion";
import { Modal, Spinner } from "reactstrap";
import { ModalBody } from "reactstrap";
import crossIcon from "./../../../assets/images/cross.svg";
import alertIcon from "./../../../assets/images/ico-alert.svg";
import { useLocation } from "react-router";
import warningIcon from "../../../assets/images/info-icon.png";
import NewConversationToast from "Messenger/pages/PatientConversation/component/NewConversationToast";
import moment from "moment";
import {
  useCheckRequestForShift,
  useEditRequestMutation,
} from "repositories/timesheet-repository";
import toast from "react-hot-toast";
import RequestModal from "./components/RequestModal";

export default function DetailView({
  userId,
  officeId,
  viewOnly,
  viewOnlyReason,
  selectedDate,
  selectedDay,
  IP,
  t,
  officeDetail,
  match,
  profile,
  updateTimesheet = false,
  isEditTimesheetScreen = false,
  myTimesheetPage = false,
  addRequestPage = false,
}) {
  const location = useLocation();
  const { data, isFetching, isLoading } = useCheckRequestForShift(
    profile?.id,
    decodeId(match.params.officeId),
    moment(selectedDate).format("YYYY-MM-DD"),
    {
      enabled: !!myTimesheetPage,
    },
  );
  const editRequestMutation = useEditRequestMutation();
  const [isRequestModalOpen, setIsRequestModalOpen] = useState(false);

  let isClockInOutOn = false;
  if (
    location.state &&
    location.state.officeData &&
    !location?.state?.staffDetail &&
    !isEditTimesheetScreen
  ) {
    isClockInOutOn = location.state.officeData.isClockInOutOn;
  }
  if (
    officeDetail &&
    officeDetail.isClockInOutOn &&
    !location?.state?.staffDetail &&
    !isEditTimesheetScreen
  ) {
    isClockInOutOn = officeDetail.isClockInOutOn;
  }
  const staffDetail = location?.state?.staffDetail || null;

  const [isRejectionReasonModalOpen, setIsRejectionReasonModalOpen] =
    useState(false);

  const shouldRenderForm = () =>
    selectedDay !== null && !selectedDay.isFutureDate;
  const shouldRenderTimesheetStatus = () =>
    shouldRenderForm() &&
    (selectedDay.isWorking ||
      (!selectedDay.isWorking && selectedDay.timesheetId));
  const shouldRenderNonWorkingDayText = () =>
    shouldRenderForm() && !selectedDay.isWorking && !selectedDay.timesheetId;
  const shouldRenderReasonLabel = () =>
    shouldRenderForm() && selectedDay.timesheetStatus === 4;
  const shouldRenderHolidayView = () => selectedDay !== null && false;
  const shouldRenderFutureDateView = () =>
    selectedDay !== null && selectedDay.isFutureDate;
  const sendUpdateRequest = async (reason) => {
    try {
      await editRequestMutation.mutateAsync({
        timesheetRequestDate: moment(selectedDate).format("YYYY-MM-DD"),
        userId,
        officeId,
        reason,
        IsForShift: !isClockInOutOn,
      });
      toast.success(t("staff.timesheetRequestSent"));
      setIsRequestModalOpen(false);
    } catch (e) {
      toast.error(e.message);
    }
  };
  const updateTimesheetFalse = useMemo(
    () => !updateTimesheet,
    [updateTimesheet],
  );
  if (isFetching || isLoading) {
    return (
      <div className="loader-container">
        <Spinner animation="border" className="loader-spinner" />
      </div>
    );
  }
  return (
    <div className={styles["timesheet-box"]}>
      {(updateTimesheet || staffDetail || selectedDay?.updatedBy) && (
        <div className="edit-profile-warning-container container mb-4 pl-0 pr-0">
          <div className="edit-profile-warning-bg ">
            <img
              className="edit-profile-warning-icon"
              src={warningIcon}
              alt="warning icon"
            />
            {updateTimesheetFalse && staffDetail && (
              <div className="edit-profile-warning-text">
                <p className={styles["text-wrap"]}>
                  {t("staff.adminEditInfo", {
                    admin: profile?.firstName + " " + profile?.lastName,
                    staff: staffDetail?.firstName + " " + staffDetail?.lastName,
                  })}
                  &nbsp;
                </p>
              </div>
            )}
            {updateTimesheet && (
              <div className="edit-profile-warning-text">
                <p className={styles["text-wrap"]}>
                  {t("staff.editTimesheetByAdmin", {
                    staff:
                      staffDetail?.name ||
                      staffDetail?.firstName + " " + staffDetail?.lastName,
                  })}
                  &nbsp;
                </p>
              </div>
            )}
            {updateTimesheetFalse && !staffDetail && selectedDay?.updatedBy && (
              <div className="edit-profile-warning-text">
                <p className={styles["text-wrap"]}>
                  {t("staff.staffEditInfo", {
                    admin:
                      selectedDay?.updatedBy?.firstName +
                      " " +
                      selectedDay?.updatedBy?.lastName,
                  })}
                  &nbsp;
                </p>
                {selectedDay?.note && (
                  <p className={styles["text-wrap"]}>
                    <strong>{t("staff.note")}</strong>: {selectedDay?.note}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      )}
      {addRequestPage &&
        !data?.status &&
        !shouldRenderHolidayView() &&
        !shouldRenderFutureDateView() && (
          <NewConversationToast
            text={t("staff.updateStaffTimesheetInfoWarning")}
          />
        )}
      <div className={styles["date-status-box"]}>
        <div className={styles["date-box"]}>
          <Text size="25px" weight="500" color="#111B45" marginBottom="10px">
            {formatDate(selectedDate, "MMMM D, YYYY")}
          </Text>
        </div>

        {updateTimesheetFalse && (
          <AnimatePresence>
            {shouldRenderTimesheetStatus() && (
              <motion.div
                animate={{ opacity: 1 }}
                initial={{ opacity: 0 }}
                exit={{ opacity: 0 }}
              >
                <TimesheetStatus selectedDay={selectedDay} />
              </motion.div>
            )}
          </AnimatePresence>
        )}

        {shouldRenderNonWorkingDayText() && (
          <Text
            size="12px"
            color="#79869a"
            className={styles["subtext-position"]}
          >
            {t("staff.thisIsANonWorkingDayForYou")}
          </Text>
        )}

        {shouldRenderReasonLabel() && (
          <Text
            size="12px"
            className={`${
              isEditTimesheetScreen ? "" : styles["subtext-position"]
            } ${styles["reason-label"]}`}
            onClick={() => setIsRejectionReasonModalOpen(true)}
          >
            {t("staff.seeReason")}
          </Text>
        )}
      </div>
      {shouldRenderForm() && viewOnlyReason && (
        <div className="d-flex align-items-start">
          <img
            src={alertIcon}
            className={"mr-1 " + styles["alert-icon"]}
            alt=""
          />
          <Text size="12px" color="#ff4550">
            {viewOnlyReason}
          </Text>
        </div>
      )}

      {shouldRenderForm() && (
        <motion.div animate={{ opacity: 1 }} initial={{ opacity: 0 }}>
          {!isClockInOutOn ? (
            <Form
              userId={userId}
              officeId={officeId}
              selectedDate={selectedDate}
              selectedDay={selectedDay}
              viewOnly={viewOnly}
              IP={IP}
              t={t}
              match={match}
              // enable request update send this key requestUpdateButtonShow
              requestUpdateButtonShow={addRequestPage && !data?.status}
              setIsRequestModalOpen={setIsRequestModalOpen}
              addRequestPage={addRequestPage}
            />
          ) : (
            <ClockInClockOutForm
              userId={userId}
              officeId={officeId}
              selectedDate={selectedDate}
              selectedDay={selectedDay}
              viewOnly={viewOnly}
              IP={IP}
              t={t}
              setIsRequestModalOpen={setIsRequestModalOpen}
            />
          )}
        </motion.div>
      )}

      {shouldRenderHolidayView() && (
        <motion.div animate={{ opacity: 1 }} initial={{ opacity: 0 }}>
          <div className={styles["applicable-box"]}>
            <h4>{selectedDay.holidayTitle}</h4>
            <p>{t("staff.timesheetHoliday")}</p>
          </div>
        </motion.div>
      )}

      {shouldRenderFutureDateView() && (
        <motion.div animate={{ opacity: 1 }} initial={{ opacity: 0 }}>
          <div className={styles["applicable-box"]}>
            <h4>{t("staff.timesheetNotApplicable")}</h4>
            <p>{t("staff.timesheetFutureDate")}</p>
          </div>
        </motion.div>
      )}

      {isRejectionReasonModalOpen && (
        <Modal
          isOpen={isRejectionReasonModalOpen}
          toggle={() => setIsRejectionReasonModalOpen(false)}
          className="modal-dialog-centered"
          modalClassName="custom-modal"
        >
          <span
            className="close-btn"
            onClick={() => setIsRejectionReasonModalOpen(false)}
          >
            <img src={crossIcon} alt="close" />
          </span>
          <ModalBody>
            <Text size="14px" weight="700">
              {t("staff.reasonForRejection")}
            </Text>
            <Text>
              {selectedDay.timesheetRejectionReason || t("staff.noReasonAdded")}
            </Text>
          </ModalBody>
        </Modal>
      )}
      {isRequestModalOpen && (
        <RequestModal
          isModalOpen={isRequestModalOpen}
          closeModal={() => setIsRequestModalOpen(false)}
          confirmSend={sendUpdateRequest}
          requestUpdateForWholeDayShift={!isClockInOutOn}
        />
      )}
    </div>
  );
}

function TimesheetStatus({ selectedDay }) {
  let statusId = 1;
  let backgroundColorClass = styles["timesheet-red"];

  if (selectedDay.type === "timesheet" || selectedDay.timesheetStatus) {
    statusId = selectedDay.timesheetStatus;
    backgroundColorClass = styles["timesheet-filled"];
  }

  if (statusId === 5 || statusId === 3) {
    backgroundColorClass = styles["timesheet-paid"];
  } else if (statusId === 4) {
    backgroundColorClass = styles["timesheet-red"];
  }

  const timesheetStatus = getTimesheetStatusById(statusId);
  return (
    <div className={`${backgroundColorClass} ${styles["status-box"]}`}>
      {timesheetStatus.title}
    </div>
  );
}
